/**
 * The accessible label for a meld or Book.
 *
 * Section 6 marks a pure Book with a red card and a wild Book with a black
 * one. That distinction is invisible to a screen reader and unreliable for
 * anyone who cannot separate the two colors, so the badge is spelled out
 * here instead of relying on hue.
 */

import {
    type Card,
    type Meld,
    type MeldableRank,
    getMeldValue,
    isBook,
    isPure
} from "@ryzun/haf-saloon-game-engine";

import { getRankWord } from "./cards.js";

export function describeMeld(meld: Meld): string {
    const kind = isBook(meld) ? (isPure(meld) ? "pure book" : "wild book") : "meld";
    const locked = isBook(meld) && isPure(meld) ? ", locked" : "";

    return `${kind} of ${getRankWord(meld.rank, true)}, ${meld.cards.length} cards, ${getMeldValue(meld)} points${locked}`;
}

/**
 * A team's tableau is split in two on the table: melds still building toward
 * a Book in front of one seat, finished Books in front of the other. `kind`
 * left undefined returns every meld, which is what the expanded tray view
 * still needs to show both halves together.
 */
export function selectMelds(melds: readonly Meld[], kind?: "books" | "melds"): readonly Meld[] {
    if (kind === "books") {
        return melds.filter(isBook);
    }

    if (kind === "melds") {
        return melds.filter((meld) => !isBook(meld));
    }

    return melds;
}

/** Each book's card ids, by rank, to compare against a later look at the table. */
export function getBookCardIds(books: readonly Meld[]): ReadonlyMap<MeldableRank, ReadonlySet<string>> {
    return new Map(books.map((book) => [book.rank, new Set(book.cards.map((card) => card.id))]));
}

/**
 * The cards each book took since `previous` was read, by rank. A book missing
 * from `previous` was only just finished - it arrives from the meld row
 * rather than growing - so it is not one of them.
 */
export function findAddedCards(
    previous: ReadonlyMap<MeldableRank, ReadonlySet<string>>,
    books: readonly Meld[]
): ReadonlyMap<MeldableRank, readonly Card[]> {
    return new Map(books.flatMap((book): [MeldableRank, readonly Card[]][] => {
        const before = previous.get(book.rank);
        const added = before === undefined ? [] : book.cards.filter((card) => !before.has(card.id));

        return added.length > 0 ? [[book.rank, added]] : [];
    }));
}
